import type { SerializedEditorState } from "lexical";

export const STORAGE_PREFIX = "cohesive:message-draft:";

/** Unsent drafts expire after 30 days. */
export const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const DRAFT_VERSION = 1 as const;

export interface MessageDraftEntry {
  version: typeof DRAFT_VERSION;
  updatedAt: number;
  editorState: SerializedEditorState;
}

export interface ResolveDraftStorageKeyParams {
  flowSessionId?: string;
  canvasId?: string;
}

/** Org/user identity string (`orgId:userId`). */
export type MessageDraftIdentity = string;

export function resolveDraftStorageKeySuffix({
  flowSessionId,
  canvasId,
}: ResolveDraftStorageKeyParams): string {
  if (flowSessionId) {
    return `session:${flowSessionId}`;
  }
  if (canvasId) {
    return `canvas:${canvasId}`;
  }
  return "home";
}

export function resolveDraftStorageKey(
  identity: MessageDraftIdentity,
  params: ResolveDraftStorageKeyParams,
): string {
  return `${STORAGE_PREFIX}${identity}:${resolveDraftStorageKeySuffix(params)}`;
}

function isExpired(updatedAt: number, now = Date.now()): boolean {
  return now - updatedAt > DRAFT_TTL_MS;
}

function isValidEntry(value: unknown): value is MessageDraftEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, unknown>;
  return (
    entry.version === DRAFT_VERSION &&
    typeof entry.updatedAt === "number" &&
    entry.editorState != null &&
    typeof entry.editorState === "object"
  );
}

/** Synchronous read; returns null if missing, invalid, or expired. */
export function readMessageDraft(key: string): MessageDraftEntry | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidEntry(parsed)) {
      localStorage.removeItem(key);
      return null;
    }
    if (isExpired(parsed.updatedAt)) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeMessageDraft(
  key: string,
  editorState: SerializedEditorState,
): void {
  const entry: MessageDraftEntry = {
    version: DRAFT_VERSION,
    updatedAt: Date.now(),
    editorState,
  };
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // quota exceeded / unavailable — ignore
  }
}

export function removeMessageDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // unavailable — ignore
  }
}

/** Drop all expired draft entries across every identity. */
export function garbageCollectMessageDrafts(now = Date.now()): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) {
        keysToRemove.push(key);
        continue;
      }
      try {
        const parsed: unknown = JSON.parse(raw);
        if (!isValidEntry(parsed) || now - parsed.updatedAt > DRAFT_TTL_MS) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // unavailable — ignore
  }
}

/** True when the serialized editor state has no meaningful text content. */
export function isEditorStateEmpty(
  editorState: SerializedEditorState,
): boolean {
  const root = editorState.root;
  if (!root || !Array.isArray(root.children)) return true;

  const hasContent = (nodes: unknown[]): boolean => {
    for (const node of nodes) {
      if (!node || typeof node !== "object") continue;
      const n = node as Record<string, unknown>;
      if (n.type === "text" && typeof n.text === "string" && n.text !== "") {
        return true;
      }
      if (
        n.type === "connection" ||
        n.type === "skill" ||
        n.type === "placeholder"
      ) {
        return true;
      }
      if (Array.isArray(n.children) && hasContent(n.children)) {
        return true;
      }
    }
    return false;
  };

  return !hasContent(root.children);
}
